import React from 'react'
import { Box, Typography } from '@mui/material'

import { AskIrisButton } from '@components'
import { MbKpiCard, MbKpiRow, MbKpiSkeleton } from 'Pages/MyBusiness/_shared/Ui'
import { dsColorNew } from '@palette'
import { pct, points, tone } from '@utils/colourScale'
import { cellBg, cellInk } from 'Pages/BusinessLogistics/Dashboards/OfficeNetwork/Page/@components/DistanceCell'
import { inUnit, money } from '../@services/amounts'
import { deltaSentence, unitWord } from '../@services/windowLabel'
import { kpiEvidence } from '../@services/askEvidence'


/** `▼ −5.4%` in the BI ink (green down, red up, grey in line); `—` without a figure. */
export function Change({ ratio, size = 26 }) {
	const t = tone(ratio)
	if (!t) return <Box component="span" sx={{ color: dsColorNew.textSubtle }}>—</Box>
	return <Box component="span" sx={{ color: cellInk(t), fontSize: size, fontWeight: 700, whiteSpace: 'nowrap' }}>{t.arrow} {pct(ratio)}</Box>
}

/** `▼ −3.8 pt vs your tier` as a soft pill in the BI colours. */
export function DeltaPill({ deltaPt, suffix = 'vs your tier' }) {
	if (deltaPt == null) return <Box component="span" sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>no tier to compare with</Box>
	const t = tone(deltaPt / 100)
	return <Box component="span" sx={{
		display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
		backgroundColor: t?.level ? cellBg(t) : dsColorNew.pillTrackBg, color: t ? cellInk(t) : dsColorNew.textMuted,
	}}>{t?.arrow} {points(deltaPt)} {suffix}</Box>
}

const ask = (card, data, view, askable, tooltip) => (askable
	? <AskIrisButton variant="icon" tooltip={tooltip} ariaLabel={tooltip} getAsk={() => kpiEvidence({ card, data, view })}/>
	: null)

/**
 * The three cards (UX: "Diferencia vs. mi tier" lives inside Your prices): Your prices, Your tier, Rates moved.
 * Every figure is the server's; the unit is applied to the money captions.
 */
export default function Headline({ data, view, loading, askable }) {
	if (loading || !data) return <Box sx={{ mb: 3 }}><MbKpiSkeleton count={3} min={220}/></Box>
	const h = data.headline || {}
	const lor = data.sample?.lor || 1
	const unit = view.unit
	const pair = p => `${money(inUnit(p?.start, unit, lor))} → ${money(inUnit(p?.end, unit, lor))}`
	const moved = h.moved || {}
	const simCells = h.own?.sim_cells || 0   // v1.5: comparable rates whose end is a simulated decision
	const nAcriss = (data.sample?.acriss || []).length, nDates = (data.sample?.dates || []).length

	return <MbKpiRow sx={{ mb: 3, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
		<MbKpiCard icon="arrow-trend-down" label="Your prices" value={<Change ratio={h.own?.change}/>}
			hint={h.own?.change == null ? 'no comparable cell in the window' : deltaSentence(h.delta_pt, h.own?.change, h.market?.change)}
			action={ask('prices', data, view, askable && h.own?.change != null, 'Ask Iris about your prices')}>
			<Typography sx={{ fontSize: 13, color: dsColorNew.textDark, mt: 1 }}>{pair(h.own)} <Box component="span" sx={{ color: dsColorNew.textMuted }}>average, {unitWord(unit)}</Box></Typography>
			<Box sx={{ mt: 1 }}><DeltaPill deltaPt={h.delta_pt}/></Box>
			{!!simCells && <Typography sx={{ fontSize: 12, color: dsColorNew.noteInfo, mt: 0.75 }}>{simCells} of {h.own?.cells} {h.own?.cells === 1 ? 'rate ends' : 'rates end'} in a simulated decision (SIM): nothing was written to the PMS for {simCells === 1 ? 'it' : 'them'}</Typography>}
		</MbKpiCard>
		<MbKpiCard icon="users" color={dsColorNew.cyan} label="Your tier" value={<Change ratio={h.market?.change}/>}
			hint={data.tier?.none ? 'the strategies compare against no tier' : `same cells, same window${(data.tier?.strategies || []).length > 1 ? ` · ${data.tier.strategies.length} tiers` : ''}`}
			action={ask('tier', data, view, askable && h.market?.change != null, 'Ask Iris about your tier')}>
			<Typography sx={{ fontSize: 13, color: dsColorNew.textDark, mt: 1 }}>{pair(h.market)} <Box component="span" sx={{ color: dsColorNew.textMuted }}>average of {data.tier?.providers || 0} providers</Box></Typography>
		</MbKpiCard>
		<MbKpiCard icon="arrows-up-down" color={dsColorNew.appBlue} label="Rates moved"
			value={moved.m ? <><Box component="span">{moved.n}</Box> <Box component="span" sx={{ fontSize: 15, fontWeight: 500, color: dsColorNew.textMuted }}>of {moved.m}</Box></> : '—'}
			hint={moved.m ? `${moved.m} cells = ${nAcriss} ACRISS × ${nDates} dates${moved.no_decision ? ` · ${moved.no_decision} without a decision` : ''}${moved.incomparable ? ` · ${moved.incomparable} not comparable` : ''}` : 'no decision in the window'}
			action={ask('moved', data, view, askable && !!moved.m, 'Ask Iris about the rates moved')}>
			{!!moved.m && <Box sx={{ display: 'flex', gap: 1.5, mt: 1, fontSize: 13, fontWeight: 600 }}>
				<Box component="span" sx={{ color: cellInk({ side: 'cheaper' }) }}>▼ {moved.down}</Box>
				<Box component="span" sx={{ color: cellInk({ side: 'dearer' }) }}>▲ {moved.up}</Box>
				<Box component="span" sx={{ color: dsColorNew.textMuted }}>= {moved.unchanged}</Box>
			</Box>}
		</MbKpiCard>
	</MbKpiRow>
}
